const initialEntityState = {
  isActive: false,
  entities: new Set(),
};

const INITIAL_STATE = {
  categories: {
    ...initialEntityState,
    otherIsDimmed: false,
  },
  subcategories: initialEntityState,
  universities: {
    ...initialEntityState,
    otherIsDimmed: false,
  },
  cities: initialEntityState,
  provinces: initialEntityState,
  recommendations: initialEntityState,
};

class HighlightedStateManager {
  constructor() {
    this.state = INITIAL_STATE;
    this.topRecommendations = null;
    this.topTags = null;
  }

  reset = () => {
    this.state = INITIAL_STATE;
  };

  registerSummary = (recommendations, allTags, topTags) => {
    this.topRecommendations = Object.values(recommendations);
    this.allTags = allTags;
    this.topTags = {};
    Object.keys(topTags).forEach(schema => {
      this.topTags[schema] = new Set(topTags[schema].map(t => t.tag.id));
    });
  };

  highlightRecommendation = recommendation => {
    let tagState = {};

    recommendation.tags.forEach(tag => {
      if (tagState[tag.schema] === undefined) {
        tagState[tag.schema] = {
          isActive: true,
          entities: new Set([tag.id]),
        };
      } else {
        tagState[tag.schema].entities.add(tag.id);
      }
    });

    ['categories', 'universities'].forEach(schema => {
      tagState[schema].otherIsDimmed = this.otherIsDimmed(
        tagState[schema].entities,
        schema
      );
    });

    this.state = {
      ...this.state,
      recommendations: {
        isActive: true,
        entities: new Set([recommendation.uuid]),
      },
      ...tagState,
    };
  };

  highlightTag = tag => {
    switch (tag.schema) {
      case 'categories':
        return this.highlightCategory(tag);
      case 'subcategories':
        return this.highlightSubcategory(tag);
      case 'universities':
        return this.highlightUniversity(tag);
      case 'cities':
        return this.highlightCity(tag);
      default:
        throw new Error('No such schema is know to the highlighting service');
    }
  };

  highlightCategory = tag => {
    let highlightedSubcategories = new Set();
    this.allTags.subcategories.forEach(subcat => {
      if (subcat.categories.find(c => c.id === tag.id)) {
        highlightedSubcategories.add(subcat.id);
      }
    });

    this.state = {
      ...INITIAL_STATE,
      recommendations: this.filterRecommendationsByTag(tag),
      subcategories: {
        isActive: true,
        entities: highlightedSubcategories,
      },
      categories: {
        isActive: true,
        entities: new Set().add(tag.id),
        otherIsDimmed: true,
      },
    };
  };

  highlightSubcategory = tag => {
    let categories = new Set(tag.categories.map(c => c.id));
    this.state = {
      ...INITIAL_STATE,
      recommendations: this.filterRecommendationsByTag(tag),
      subcategories: { isActive: true, entities: new Set().add(tag.id) },
      categories: {
        isActive: true,
        entities: categories,
        otherIsDimmed: this.otherIsDimmed(categories, 'categories'),
      },
    };
  };

  highlightUniversity = university => {
    this.state = {
      ...INITIAL_STATE,
      recommendations: this.filterRecommendationsByTag(university),
      universities: {
        isActive: true,
        entities: new Set().add(university.id),
        otherIsDimmed: true,
      },
      cities: { isActive: true, entities: new Set().add(university.city.id) },
    };
  };

  highlightCity = city => {
    const universities = new Set();
    this.allTags.universities.forEach(university => {
      if (university.city.id === city.id) {
        universities.add(university.id);
      }
    });

    this.state = {
      ...INITIAL_STATE,
      recommendations: this.filterRecommendationsByTag(city),
      universities: {
        isActive: true,
        entities: universities,
        otherIsDimmed: this.otherIsDimmed(universities, 'universities'),
      },
      cities: { isActive: true, entities: new Set().add(city.id) },
    };
  };

  filterRecommendationsByTag = tag => {
    const recommendations = new Set();
    this.topRecommendations.forEach(rec => {
      let tagMatch = !!rec.tags.find(
        rTag => tag.schema === rTag.schema && tag.id === rTag.id
      );
      if (tagMatch) {
        recommendations.add(rec.uuid);
      }
    });
    return {
      isActive: true,
      entities: recommendations,
    };
  };

  otherIsDimmed = (entities, schema) => {
    return ![...entities].find(id => {
      return !this.topTags[schema].has(id);
    });
  };
}

export default HighlightedStateManager;



// WEBPACK FOOTER //
// ./src/services/highlightingService/stateManager.js